// src/components/Warehouses/WarehouseDetail.js - Updated Router Component
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import MintsoftWarehouseDetail from './Mintsoft/MintsoftWarehouseDetail';
import ITBWarehouseDetail from './ITB/ITBWarehouseDetail';
// import ShipbobWarehouseDetail from './Shipbob/ShipbobWarehouseDetail';
// import AmazonWarehouseDetail from './Amazon/AmazonWarehouseDetail';

const WarehouseDetail = () => {
  const { warehouseId } = useParams();
  const [warehouseType, setWarehouseType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Determine warehouse type from configuration
  const determineWarehouseType = async () => {
    try {
      setLoading(true);
      console.log(`🔍 Determining warehouse type for ID: ${warehouseId}`);

      // Get warehouse configuration to determine type
      const response = await fetch('${process.env.REACT_APP_API_URL}/api/dashboard/warehouses/config', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch warehouse configuration');
      }

      const data = await response.json();
      console.log('🔍 Full warehouse config data:', data);

      const warehouse = data.warehouses?.find(w => w.id.toString() === warehouseId.toString());
      console.log('🔍 Found warehouse:', warehouse);

      if (!warehouse) {
        throw new Error(`Warehouse ${warehouseId} not found in configuration`);
      }

      // Determine warehouse type from warehouse configuration
      let type = 'unknown';

      // Check for warehouse type
      if (warehouse.warehouse_type === 'mintsoft') {
        type = 'mintsoft';
      } else if (warehouse.warehouse_type === 'deposco' || warehouse.warehouse_type === 'itb') {
        type = 'itb';
      } else if (warehouse.name && warehouse.name.toLowerCase().includes('mintsoft')) {
        type = 'mintsoft';
      } else if (warehouse.name && warehouse.name.toLowerCase().includes('itb')) {
        type = 'itb';
      } else if (warehouse.name && warehouse.name.toLowerCase().includes('uk')) {
        // UK warehouse uses Mintsoft
        type = 'mintsoft';
      } else if (warehouse.api_config?.base_url?.includes('mintsoft.co.uk')) {
        type = 'mintsoft';
      } else if (warehouse.api_config?.base_url?.includes('deposco.com')) {
        type = 'itb';
      } else {
        console.log('⚠️ Could not determine type, checking API structure');
        // Default based on location
        if (warehouse.country === 'USA' || warehouse.location?.includes('Georgia')) {
          type = 'itb';
        } else {
          type = 'mintsoft';
        }
      }

      console.log(`✅ Detected warehouse type: ${type} for warehouse: ${warehouse.name}`);
      setWarehouseType(type);

    } catch (error) {
      console.error('❌ Error determining warehouse type:', error);
      setError('Failed to determine warehouse type: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    determineWarehouseType();
  }, [warehouseId]);

  // Loading state
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div style={{textAlign: 'center', padding: '2rem'}}>
            <div className="loading-spinner" style={{margin: '0 auto'}}></div>
            <p style={{marginTop: '1rem', color: '#6b7280'}}>
              Determining warehouse type...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div className="error-message">
            <AlertTriangle size={24} />
            <div>
              <h3>Error Loading Warehouse</h3>
              <p className="error-text">{error}</p>
              <button
                onClick={determineWarehouseType}
                className="btn-primary"
                style={{marginTop: '1rem', width: 'auto'}}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Route to specific warehouse detail component based on type
  switch (warehouseType) {
    case 'mintsoft':
      return <MintsoftWarehouseDetail />;

    case 'itb':
    case 'deposco':
      return <ITBWarehouseDetail />;

    // Future warehouse types can be added here:
    // case 'shipbob':
    //   return <ShipbobWarehouseDetail />;
    // case 'amazon':
    //   return <AmazonWarehouseDetail />;

    default:
      return (
        <div className="dashboard-container">
          <div className="dashboard-main">
            <div className="error-message">
              <AlertTriangle size={24} />
              <div>
                <h3>Unsupported Warehouse Type</h3>
                <p className="error-text">
                  Warehouse type "{warehouseType}" is not supported yet.
                  Currently supported: Mintsoft, ITB/Deposco
                </p>
                <Link to="/dashboard" className="btn-primary" style={{marginTop: '1rem'}}>
                  Back to Dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
  }
};

export default WarehouseDetail;